import React, { useState, useMemo, useEffect } from "react";
import * as XLSX from "xlsx";
import {
  useGetRegistrySearchUsageQuery,
  useGetRegistrySearchUsageByOrgQuery,
  useMarkRegistrySearchMonthPaidMutation,
} from "apis/registrySearch";
import PageLoader from "components/ui/PageLoader";
import { Modal } from "@beleef/ui";
import Table from "components/ui/Table";
import Button from "components/ui/Button";
import SearchSelect from "components/ui/SearchSelect";
import EditEntryModal from "components/registrySearch/EditEntryModal";
import { formatCurrency, formatDateTime } from "utils/helperFunctions";
import { formatType, getMonthOptionsFromEntries } from "utils/registrySearch";
import { FiEdit2 } from "react-icons/fi";
import { MdOutlineDownload } from "react-icons/md";

// ─── Shared presentational components ───────────────────────────────────────

const StatCard = ({ label, value, color = "text-darkergray" }) => (
  <div className="bg-white border border-mediumgray rounded-lg p-3 text-center">
    <div className={`text-xl font-bold ${color}`}>{value}</div>
    <div className="text-xs text-darkgray uppercase tracking-wide">{label}</div>
  </div>
);

const PaidBadge = ({ paid }) => (
  <span
    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
      paid ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
    }`}
  >
    {paid ? "Paid" : "Unpaid"}
  </span>
);

// ─── Org Detail Modal ────────────────────────────────────────────────────────

const OrgDetailModal = ({ org, isOpen, onClose }) => {
  const [editEntry, setEditEntry] = useState(null);
  const [billMonth, setBillMonth] = useState(null);
  const [confirmMarkPaid, setConfirmMarkPaid] = useState(false);

  const { summary, entries, isPending } = useGetRegistrySearchUsageByOrgQuery(
    isOpen ? org?.organizationId : null
  );
  const { mutate: markMonthPaid, isPending: isMarkingPaid } = useMarkRegistrySearchMonthPaidMutation();

  // This component stays mounted (parent just renders it with isOpen=false)
  // between opens, so clear any leftover UI state whenever it closes.
  useEffect(() => {
    if (!isOpen) {
      setEditEntry(null);
      setConfirmMarkPaid(false);
    }
  }, [isOpen]);

  const monthOptions = useMemo(() => getMonthOptionsFromEntries(entries), [entries]);

  // Keep the selected month valid as entries load in / change.
  useEffect(() => {
    if (!monthOptions.length) {
      setBillMonth(null);
      return;
    }
    if (!billMonth || !monthOptions.some((o) => o.value === billMonth.value)) {
      setBillMonth(monthOptions[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthOptions]);

  const unpaidEntriesInMonth = useMemo(() => {
    if (!billMonth) return [];
    const [year, month] = billMonth.value.split("-").map(Number);
    return entries.filter((e) => {
      if (e.paid) return false;
      const d = new Date(e.createdAt);
      return d.getFullYear() === year && d.getMonth() + 1 === month;
    });
  }, [entries, billMonth]);

  const handleMarkMonthPaid = () => {
    markMonthPaid(
      { organizationId: org.organizationId, month: billMonth.value },
      { onSettled: () => setConfirmMarkPaid(false) }
    );
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "createdAt",
        header: "Date",
        cell: (row) => (
          <span className="text-xs whitespace-nowrap">
            {formatDateTime(row.createdAt) ?? "-"}
          </span>
        ),
        isFilter: false,
        minWidth: "160px",
      },
      {
        accessorKey: "type",
        header: "Type",
        cell: (row) => (
          <span className="px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-800">
            {formatType(row.type)}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "cost",
        header: "Cost",
        cell: (row) => (
          <span className="text-xs font-medium">{formatCurrency(row.cost)}</span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "paid",
        header: "Status",
        cell: (row) => <PaidBadge paid={row.paid} />,
        isFilter: false,
      },
      {
        accessorKey: "acn",
        header: "ACN",
        cell: (row) => <span className="text-xs">{row.acn || "-"}</span>,
        isFilter: false,
      },
      {
        accessorKey: "user",
        header: "User",
        cell: (row) => (
          <div className="text-xs">
            <div className="font-medium text-darkergray">{row.user?.name || "-"}</div>
            {row.user?.email && <div className="text-darkgray">{row.user.email}</div>}
          </div>
        ),
        isFilter: false,
        minWidth: "160px",
      },
      {
        accessorKey: "property",
        header: "Property",
        cell: (row) => (
          <span className="text-xs text-darkgray">
            {row.property?.address || row.property?._id || "-"}
          </span>
        ),
        isFilter: false,
        maxWidth: "220px",
      },
      {
        accessorKey: "actions",
        header: "",
        cell: (row) => (
          <button
            className="edit-icon"
            onClick={(e) => {
              e.stopPropagation();
              setEditEntry(row);
            }}
            title="Edit record"
          >
            <FiEdit2 size={14} />
          </button>
        ),
        isFilter: false,
        stopPropagation: true,
      },
    ],
    []
  );

  const handleGenerateBill = () => {
    if (!billMonth) return;
    const [year, month] = billMonth.value.split("-").map(Number);
    const monthEntries = entries.filter((e) => {
      const d = new Date(e.createdAt);
      return d.getFullYear() === year && d.getMonth() + 1 === month;
    });

    const rows = monthEntries.map((e) => ({
      Date: formatDateTime(e.createdAt),
      Type: formatType(e.type),
      Cost: e.cost,
      Status: e.paid ? "Paid" : "Unpaid",
      ACN: e.acn || "",
      Property: e.property?.address || "",
      User: e.user?.name || "",
      Email: e.user?.email || "",
    }));

    const total = monthEntries.reduce((sum, e) => sum + (e.cost || 0), 0);
    const paid = monthEntries.filter((e) => e.paid).reduce((s, e) => s + (e.cost || 0), 0);
    const unpaid = total - paid;

    rows.push({});
    rows.push({ Date: "TOTAL", Cost: total, Status: `Paid: $${paid.toFixed(2)} | Unpaid: $${unpaid.toFixed(2)}` });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bill");
    XLSX.writeFile(wb, `RegistrySearch-Bill-${org?.companyName || org?.organizationId}-${billMonth.label}.xlsx`);
  };

  if (!org) return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`${org.companyName || "Organisation"} — Usage Detail`}
        className="max-w-5xl"
      >
        <div className="space-y-5">
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard label="Total Searches" value={summary.totalSearches} />
              <StatCard label="Total Amount" value={formatCurrency(summary.totalAmount)} />
              <StatCard label="Paid" value={formatCurrency(summary.paidAmount)} color="text-green-700" />
              <StatCard label="Unpaid" value={formatCurrency(summary.unpaidAmount)} color="text-red-600" />
            </div>
          )}

          <div className="bg-white border border-mediumgray rounded-lg p-4">
            <h2 className="text-sm font-semibold text-darkgray mb-3">Monthly Billing</h2>
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-darkergray mb-1">Month</label>
                <SearchSelect
                  options={monthOptions}
                  value={billMonth}
                  onChange={(opt) => setBillMonth(opt || null)}
                  searchable={false}
                  placeholder={monthOptions.length ? "Select month" : "No records yet"}
                  disabled={!monthOptions.length}
                />
              </div>
              <Button
                className="gray-button flex items-center gap-2 whitespace-nowrap"
                onClick={() => setConfirmMarkPaid(true)}
                disabled={!billMonth || !unpaidEntriesInMonth.length}
              >
                Mark Month as Paid
                {billMonth ? ` (${unpaidEntriesInMonth.length})` : ""}
              </Button>
              <Button
                className="black-button flex items-center gap-2 whitespace-nowrap"
                onClick={handleGenerateBill}
                disabled={!billMonth}
              >
                <MdOutlineDownload size={16} />
                Export Bill
              </Button>
            </div>
          </div>

          {isPending ? (
            <div className="text-center py-8 text-darkgray text-sm">Loading entries…</div>
          ) : (
            <Table
              title="Search History"
              columns={columns}
              data={entries}
              pageSize={15}
              searchable={false}
              exportable={true}
              exportFilename={`registrySearch-${org.companyName || org.organizationId}`}
              emptyText="No search history found."
            />
          )}
        </div>
      </Modal>

      <EditEntryModal
        entry={editEntry}
        organizationId={org?.organizationId}
        isOpen={!!editEntry}
        onClose={() => setEditEntry(null)}
      />

      <Modal
        isOpen={confirmMarkPaid}
        onClose={() => setConfirmMarkPaid(false)}
        title="Mark Month as Paid"
      >
        <div className="flex flex-col space-y-4">
          <p>
            Mark all {unpaidEntriesInMonth.length} unpaid record
            {unpaidEntriesInMonth.length === 1 ? "" : "s"} for{" "}
            <strong>{billMonth?.label}</strong> as paid?
          </p>
          <p className="italic text-sm text-darkgray">
            <strong>Note:</strong> This cannot be undone.
          </p>
          <div className="w-full flex gap-2 justify-end">
            <Button onClick={() => setConfirmMarkPaid(false)} className="gray-button">
              Cancel
            </Button>
            <Button
              onClick={handleMarkMonthPaid}
              className="black-button"
              loading={isMarkingPaid}
            >
              Yes, mark paid
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

const RegistrySearchUsage = () => {
  const [selectedOrg, setSelectedOrg] = useState(null);

  const { summary, companies, isPending } = useGetRegistrySearchUsageQuery();

  const columns = useMemo(
    () => [
      {
        accessorKey: "companyName",
        header: "Organisation",
        cell: (row) => (
          <span className="font-medium text-darkergray">
            {row.companyName || row.organizationId}
          </span>
        ),
        isFilter: true,
        minWidth: "200px",
      },
      {
        accessorKey: "totalSearches",
        header: "Total Searches",
        cell: (row) => row.totalSearches,
        isFilter: false,
      },
      {
        accessorKey: "totalAmount",
        header: "Total Amount",
        cell: (row) => (
          <span className="font-medium">{formatCurrency(row.totalAmount)}</span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "paidAmount",
        header: "Paid",
        cell: (row) => (
          <span className="text-green-700 font-medium">
            {formatCurrency(row.paidAmount)}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "unpaidAmount",
        header: "Unpaid",
        cell: (row) => (
          <span className="text-red-600 font-medium">
            {formatCurrency(row.unpaidAmount)}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "breakdown",
        header: "Breakdown",
        cell: (row) =>
          row.breakdown
            ? Object.entries(row.breakdown)
                .map(([k, v]) => `${formatType(k)}: ${v}`)
                .join(", ")
            : "-",
        isFilter: false,
        maxWidth: "260px",
      },
      {
        accessorKey: "lastSearchAt",
        header: "Last Search",
        cell: (row) => (
          <span className="text-xs text-darkgray whitespace-nowrap">
            {formatDateTime(row.lastSearchAt) ?? "-"}
          </span>
        ),
        isFilter: false,
        minWidth: "160px",
      },
    ],
    []
  );

  if (isPending) return <PageLoader />;

  return (
    <>
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold text-darkergray mb-2">Registry Search Usage</h1>
        <p className="text-sm text-darkgray mb-6">
          Overview of Registry Search usage and billing across all organisations.
          Click a row to view detailed search history and generate monthly bills.
        </p>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <StatCard label="Total Searches" value={summary.totalSearches} />
            <StatCard label="Total Amount" value={formatCurrency(summary.totalAmount)} />
            <StatCard label="Paid" value={formatCurrency(summary.paidAmount)} color="text-green-700" />
            <StatCard label="Unpaid" value={formatCurrency(summary.unpaidAmount)} color="text-red-600" />
          </div>
        )}

        <Table
          title="Organisations"
          columns={columns}
          data={companies}
          pageSize={20}
          searchable={true}
          exportable={true}
          exportFilename="registrySearch-usage"
          onRowClick={(row) => setSelectedOrg(row)}
          emptyText="No usage data found."
        />
      </div>

      <OrgDetailModal
        org={selectedOrg}
        isOpen={!!selectedOrg}
        onClose={() => setSelectedOrg(null)}
      />
    </>
  );
};

export default RegistrySearchUsage;
